/**
 * Wealth → CFO: Liam's private finance office. He talks, drops files in, and the agent learns — live figures from the app,
 * the documents, and a facts ledger he approves. The server is the source of truth (encrypted); nothing is kept in the
 * browser except the Web toggle. Ported from CEO Tasks' CfoView.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Brain, Check, ChevronRight, Cloud, CloudOff, FileText, FolderOpen, Globe, Loader2, Lock, MessageSquare, Paperclip, Pencil, Plus, Send, Sparkles, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { eur, WK } from "./shared";
import { cfo, CK, cfoErr, fmtOnDay, fmtTime, modelName, readFile, type CfoFact, type CfoFile, type CfoMessage, type CfoSession, type CfoState } from "./cfoApi";
import { CfoMarkdown } from "./CfoMarkdown";
import { FactRow, FileCard, iconBtn, Library, mini, sideLabel } from "./CfoParts";

const WEB_PREF = "ceo-cfo-web-v1";
const STARTERS = ["Where do I stand today, in five lines?", "What is still owed to the companies, and by whom?", "What should I prepare before I meet my accountant?", "Which documents are you missing to know my finances properly?"];
const warn = "text-red-700";

export function CfoTab({ isPhone }: { isPhone: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: CK,
    queryFn: cfo.state,
    // files being read: poll until every one has its filing proposal
    refetchInterval: (query) => (query.state.data?.files.some((f) => f.status === "reading") ? 2000 : false),
    refetchOnWindowFocus: false,
  });
  const state = q.data;
  const load = () => qc.invalidateQueries({ queryKey: CK });

  const [area, setArea] = useState<"chat" | "library" | "memory">("chat");
  const [session, setSession] = useState<CfoSession | null>(null);
  const [input, setInput] = useState("");
  const [deep, setDeep] = useState(false);
  // internet access is a per-browser preference, on by default
  const [web, setWeb] = useState(() => { try { return localStorage.getItem(WEB_PREF) !== "off"; } catch { return true; } });
  const toggleWeb = (on: boolean) => { setWeb(on); try { localStorage.setItem(WEB_PREF, on ? "on" : "off"); } catch { /* private window */ } };
  const [draftFiles, setDraftFiles] = useState<string[]>([]); // ids uploaded for the next message
  const [busy, setBusy] = useState<"" | "upload" | "thinking">("");
  const [waited, setWaited] = useState(0);
  const [sendErr, setSendErr] = useState("");
  const [newFact, setNewFact] = useState("");
  const [dragging, setDragging] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [session?.messages?.length, busy]);
  // an answer can take up to ~3 minutes: count the seconds so the wait never looks frozen
  useEffect(() => {
    if (busy !== "thinking") return;
    setWaited(0);
    const t = setInterval(() => setWaited((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [busy]);

  const files = useMemo(() => state?.files ?? [], [state?.files]);
  const fileById = useMemo(() => new Map(files.map((f) => [f.id, f])), [files]);
  const facts = state?.facts ?? [];
  const proposed = facts.filter((f) => f.status === "proposed");
  const confirmed = facts.filter((f) => f.status === "confirmed");
  const toFile = files.filter((f) => f.status === "proposed");

  const openSession = async (id: string) => {
    setArea("chat"); setSendErr("");
    try { setSession((await cfo.getSession(id)).session); } catch (e) { setSendErr(cfoErr(e)); }
  };
  const newSession = () => { setSession(null); setArea("chat"); setDraftFiles([]); setSendErr(""); setTimeout(() => taRef.current?.focus(), 50); };
  const removeSession = async (s: { id: string; title: string }) => {
    if (!window.confirm(`Delete the conversation "${s.title}"? Files and facts stay.`)) return;
    await cfo.deleteSession(s.id).catch((e) => window.alert(cfoErr(e)));
    if (session?.id === s.id) setSession(null);
    void load();
  };
  const renameSession = async (s: { id: string; title: string }) => {
    const title = window.prompt("Rename the conversation", s.title)?.trim();
    if (!title || title === s.title) return;
    try {
      await cfo.renameSession(s.id, title);
      setSession((cur) => (cur?.id === s.id ? { ...cur, title } : cur));
      void load();
    } catch (e) { window.alert(cfoErr(e)); }
  };

  const upload = async (list: FileList) => {
    const picked = [...list].slice(0, 10);
    if (!picked.length) return;
    const big = picked.find((f) => f.size > 25 * 1024 * 1024);
    if (big) { setSendErr(`${big.name} is larger than 25 MB.`); return; }
    setBusy("upload"); setSendErr("");
    try {
      const payload = await Promise.all(picked.map(readFile));
      const r = await cfo.upload(payload, session?.id);
      const bad = r.files.filter((f) => f.error);
      if (bad.length) setSendErr(bad.map((f) => f.error).join(" · "));
      setDraftFiles((d) => [...d, ...r.files.filter((f) => f.id).map((f) => f.id!)]);
      await load();
    } catch (e) { setSendErr(cfoErr(e)); }
    setBusy("");
  };

  const send = async () => {
    const text = input.trim();
    if ((!text && !draftFiles.length) || busy) return;
    setSendErr("");
    const attach = draftFiles;
    setInput(""); setDraftFiles([]);
    if (taRef.current) taRef.current.style.height = "auto";
    setBusy("thinking");
    let s = session;
    try {
      if (!s) s = (await cfo.newSession()).session;
      const base = s;
      const mine: CfoMessage = { role: "user", content: text || "Please read what I attached and tell me what matters.", ts: new Date().toISOString(), files: attach };
      setSession({ ...base, messages: [...(base.messages || []), mine] });
      const r = await cfo.send(base.id, text, attach, deep, web);
      setSession((cur) => ({ ...(cur || base), title: r.title, messages: [...(cur?.messages || []).filter((m) => m !== mine), r.user, r.message] }));
      void load();
    } catch (e) {
      // a 502 still saved the question: show the reason and reload the thread
      setSendErr(cfoErr(e));
      if (s) cfo.getSession(s.id).then((r) => setSession(r.session)).catch(() => {});
      void load();
    }
    setBusy("");
  };

  const decideFacts = async (ids: string[], yes: boolean) => {
    try {
      const r = await cfo.facts(yes ? { confirm: ids } : { reject: ids });
      qc.setQueryData<CfoState>(CK, (st) => (st ? { ...st, facts: r.facts } : st));
    } catch (e) { window.alert(cfoErr(e)); }
  };
  const addFact = () => cfo.facts({ text: newFact }).then(() => { setNewFact(""); void load(); }).catch((e) => window.alert(cfoErr(e)));
  const removeFile = async (f: CfoFile) => {
    if (!window.confirm(`Delete "${f.name}" from the library? This cannot be undone.`)) return;
    await cfo.deleteFile(f.id).catch((e) => window.alert(cfoErr(e)));
    setDraftFiles((d) => d.filter((id) => id !== f.id));
    void load();
  };
  const applyAction = async (m: CfoMessage, idx: number, a: { name: string; value: number; note?: string }) => {
    if (m.applied?.[idx] || !session) return;
    try {
      const r = await cfo.patchAsset({ name: a.name, value: a.value, notes: a.note, method: "told to the CFO" });
      const label = `${r.asset.name}: ${eur(r.before)} → ${eur(r.asset.value)}`;
      await Promise.all([WK.doc, WK.strategy, WK.review, WK.reviewStatus].map((queryKey) => qc.invalidateQueries({ queryKey })));
      await cfo.markApplied(session.id, m.ts, idx, label);
      setSession((cur) => (cur ? { ...cur, messages: cur.messages.map((x) => (x.ts === m.ts ? { ...x, applied: { ...(x.applied || {}), [idx]: label } } : x)) } : cur));
    } catch (e) { window.alert(`Could not update: ${cfoErr(e)}`); }
  };

  if (q.error && !state) return <p className="text-sm text-red-600">{cfoErr(q.error)}</p>;
  if (!state) return <p className="muted text-sm"><Loader2 size={13} className="mr-1 inline animate-spin" /> Opening the CFO office…</p>;

  const drive = state.drive;
  const fileCard = (f: CfoFile) => <FileCard key={f.id} f={f} folders={state.folders || []} onChanged={() => void load()} onDelete={removeFile} />;
  const factRow = (k: CfoFact) => <FactRow key={k.id} k={k} onDecide={(ids, yes) => void decideFacts(ids, yes)} onChanged={() => void load()} />;
  const navBtn = (on: boolean) => clsx("flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[0.8125rem]", on ? "bg-brand-50 font-semibold text-brand-600" : "text-[var(--text-2)] hover:bg-[var(--panel-2)]");
  const badge = "badge ml-auto bg-amber-100 text-amber-800";
  const count = (n: number) => <span className="text-[11.5px] font-normal text-[var(--subtle)]">{n}</span>;

  return (
    <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-[230px_minmax(0,1fr)]">
      <aside className="panel flex min-h-0 flex-col gap-1.5 p-3 sm:sticky sm:top-2 sm:max-h-[calc(100vh-250px)] sm:min-h-[300px]">
        <button className="btn btn-primary btn-sm" onClick={newSession}><Plus size={13} /> New conversation</button>
        <nav className="mt-1 flex flex-col gap-0.5">
          <button className={navBtn(area === "chat")} onClick={() => setArea("chat")}><MessageSquare size={14} /> Conversations</button>
          <button className={navBtn(area === "library")} onClick={() => setArea("library")}>
            <FolderOpen size={14} /> Library {count(files.length)}{toFile.length > 0 && <span className={badge}>{toFile.length} to file</span>}
          </button>
          <button className={navBtn(area === "memory")} onClick={() => setArea("memory")}>
            <Brain size={14} /> What I know {count(confirmed.length)}{proposed.length > 0 && <span className={badge}>{proposed.length} new</span>}
          </button>
        </nav>
        <div className={sideLabel}>Recent</div>
        <div className="scroll-thin max-h-[150px] min-h-10 flex-1 overflow-y-auto sm:max-h-none">
          {state.sessions.slice(0, isPhone ? 5 : 30).map((s) => (
            <div key={s.id} className={clsx("group flex items-center rounded-md", session?.id === s.id && "bg-[var(--panel-2)] ring-1 ring-[var(--line)]")}>
              <button className="flex min-w-0 flex-1 flex-col px-1.5 py-1 text-left text-[0.8125rem]" onClick={() => void openSession(s.id)} title={s.title}>
                <span className="truncate">{s.title}</span>
                <span className="muted text-[11px]">{fmtOnDay(s.updatedAt)}</span>
              </button>
              <button className={iconBtn} title="Rename conversation" onClick={() => void renameSession(s)}><Pencil size={11} /></button>
              <button className={iconBtn} title="Delete conversation" onClick={() => void removeSession(s)}><X size={12} /></button>
            </div>
          ))}
          {!state.sessions.length && <p className="muted text-xs">No conversations yet.</p>}
        </div>
        <div className="flex flex-col gap-1 border-t border-[var(--line)] pt-2 text-[11px] text-[var(--muted)] [&>span]:flex [&>span]:items-center [&>span]:gap-1.5">
          <span><Lock size={11} /> {state.encrypted ? "Encrypted · only you" : "Only you"}</span>
          {state.fusionDocs?.on ? (
            <span title={state.fusionDocs.lastError || "Filed documents are kept in FusionDocs under your name, visible only to you"} className={clsx(state.fusionDocs.lastError && "!text-red-700")}>
              <FolderOpen size={11} /> {state.fusionDocs.accessReady ? `FusionDocs: ${state.fusionDocs.filed} filed${state.fusionDocs.pending ? `, ${state.fusionDocs.pending} waiting` : ""} · only you` : "FusionDocs: getting ready"}
            </span>
          ) : (
          <span title={drive.note || drive.lastError || `Copies go to ${drive.root} in ${drive.account}`} className={clsx((drive.needsSignIn || drive.quotaFull) && "!text-red-700")}>
            {drive.connected ? (
              <><Cloud size={11} /> {drive.needsSignIn ? "Google Drive: sign-in needed" : drive.quotaFull ? "Google Drive is full" : `Google Drive: ${drive.mirrored} copied${drive.pending ? `, ${drive.pending} waiting` : ""}`}</>
            ) : (
              <><CloudOff size={11} /> Google Drive: not connected yet</>
            )}
          </span>
          )}
          <span title={`Deep: ${modelName(state.models.deep)} · filing: ${modelName(state.models.filing)} · web search: ${modelName(state.models.search)} · fallback: ${modelName(state.models.fallback)}`}>
            <Sparkles size={11} /> {modelName(state.models.chat)} via the gateway
          </span>
          {state.usage && state.usage.calls > 0 && (
            <span title={`${state.usage.rows.map((r) => `${modelName(r.model)}: ${r.calls} calls, ${r.in.toLocaleString()} in / ${r.out.toLocaleString()} out${r.costUsd != null ? `, $${r.costUsd.toFixed(2)}` : ""}`).join(" · ")}. ${state.usage.note}`}>
              This month: {state.usage.calls} calls · about ${state.usage.costUsd.toFixed(2)}
            </span>
          )}
        </div>
      </aside>

      <section className="panel min-h-[440px] min-w-0">
        {area === "chat" && (
          <div
            className={clsx("flex h-[calc(100dvh-120px)] min-h-[440px] flex-col px-3.5 py-3 sm:h-[calc(100vh-250px)]", dragging && "rounded-[14px] outline-2 -outline-offset-[6px] outline-dashed outline-brand-600")}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); void upload(e.dataTransfer.files); }}
          >
            <div className="scroll-thin flex-1 overflow-y-auto pr-1">
              {!session?.messages?.length && (
                <div className="mx-auto my-10 max-w-[560px] text-center text-sm text-[var(--text-2)]">
                  <h3 className="mb-1.5 text-[17px] font-bold text-[var(--text)]">Your CFO office</h3>
                  <p>Ask about your money or your companies, or drop in a document: accounts, a bank statement, a contract, a tax return. I answer from your live figures and from what you have given me, and I say where each number comes from.</p>
                  <div className="mt-3.5 flex flex-wrap justify-center gap-1.5">
                    {STARTERS.map((s) => <button key={s} className="chip cursor-pointer whitespace-normal text-left hover:border-brand-200" onClick={() => { setInput(s); taRef.current?.focus(); }}>{s}</button>)}
                  </div>
                </div>
              )}
              {(session?.messages || []).map((m) => (
                <div key={m.ts + m.role} className={clsx("my-2.5 flex flex-col", m.role === "user" && "items-end")}>
                  {(m.files || []).map((id) => fileById.get(id)).filter((f): f is CfoFile => !!f).map(fileCard)}
                  {(m.removedFiles || []).map((n) => <div key={n} className="muted my-0.5 flex items-center gap-2 rounded-lg border border-[var(--line)] px-2.5 py-1.5 text-[0.8125rem]"><FileText size={14} /> {n} (deleted)</div>)}
                  {m.role === "user" ? (
                    <div className="max-w-[92%] whitespace-pre-wrap break-words rounded-xl bg-brand-50 px-3 py-2 text-[0.84rem] leading-relaxed sm:max-w-[78%]">{m.content}</div>
                  ) : (
                    <Answer m={m} facts={facts} factRow={factRow} onRememberAll={(ids) => void decideFacts(ids, true)} onApply={(i, a) => void applyAction(m, i, a)} />
                  )}
                </div>
              ))}
              {busy === "thinking" && (
                <div className="my-2.5 flex">
                  <div className="muted rounded-xl bg-[var(--panel-2)] px-3 py-2 text-[0.84rem]">
                    <Loader2 size={13} className="mr-1 inline animate-spin" />
                    {deep ? "Working through it — this can take a minute…" : web ? "Thinking — looking things up on the web if the question needs it…" : "Thinking…"}
                    {waited >= 5 && <span className="num"> {waited}s</span>}
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
            {sendErr && <p className="my-1 text-sm text-red-600">{sendErr}</p>}
            {draftFiles.length > 0 && (
              <div className="my-1 flex flex-wrap gap-1.5">
                {draftFiles.map((id) => fileById.get(id)).filter((f): f is CfoFile => !!f).map((f) => (
                  <span key={f.id} className="inline-flex max-w-full items-center gap-1 rounded-full bg-[var(--panel-2)] py-0.5 pl-2 pr-1 text-[11.5px] ring-1 ring-[var(--line)]">
                    {f.status === "reading" ? <Loader2 size={11} className="animate-spin" /> : <FileText size={11} />}
                    <span className="truncate">{f.origName}</span>
                    <button aria-label="Remove" className="px-1 text-sm leading-none" onClick={() => setDraftFiles((d) => d.filter((x) => x !== f.id))}>×</button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-end gap-2 rounded-xl border border-[var(--line-strong)] px-2 py-1.5 sm:flex-nowrap">
              <input ref={fileRef} type="file" multiple hidden onChange={(e) => { if (e.target.files) void upload(e.target.files); e.target.value = ""; }} />
              <button className={clsx(iconBtn, "mb-0.5")} title="Attach files (PDF, Word, Excel, photos — up to 25 MB each)" disabled={busy === "upload"} onClick={() => fileRef.current?.click()}>
                {busy === "upload" ? <Loader2 size={15} className="animate-spin" /> : <Paperclip size={15} />}
              </button>
              <textarea
                ref={taRef}
                rows={1}
                value={input}
                placeholder="Ask, or drop a file here"
                className="max-h-40 min-w-0 flex-1 basis-[calc(100%-3rem)] resize-none border-none bg-transparent px-0.5 py-1 text-[0.875rem] leading-snug outline-none sm:basis-auto"
                onChange={(e) => { setInput(e.target.value); e.target.style.height = "auto"; e.target.style.height = `${Math.min(160, e.target.scrollHeight)}px`; }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }}
                onPaste={(e) => { if (e.clipboardData?.files?.length) { e.preventDefault(); void upload(e.clipboardData.files); } }}
              />
              <div className="ml-auto flex items-center gap-2">
                <label className="flex cursor-pointer items-center gap-1 whitespace-nowrap pb-1 text-xs text-[var(--text-2)]" title="Web: when a question needs public information (rates, tax rules, prices, news) the CFO looks it up and shows its sources. Your own figures are never put into a search.">
                  <input type="checkbox" checked={web} onChange={(e) => toggleWeb(e.target.checked)} /> Web
                </label>
                <label className="flex cursor-pointer items-center gap-1 whitespace-nowrap pb-1 text-xs text-[var(--text-2)]" title={`Deep: ${modelName(state.models.deep)} reads more and reasons longer. Use it for accounts, contracts and reports.`}>
                  <input type="checkbox" checked={deep} onChange={(e) => setDeep(e.target.checked)} /> Deep
                </label>
                <button className="btn btn-primary btn-sm" disabled={!!busy || (!input.trim() && !draftFiles.length)} onClick={() => void send()}><Send size={13} /> Send</button>
              </div>
            </div>
          </div>
        )}

        {area === "library" && <Library state={state} busy={busy} err={sendErr} upload={(l) => void upload(l)} onChanged={() => void load()} onDelete={(f) => void removeFile(f)} />}

        {area === "memory" && (
          <div className="px-4 py-3.5">
            <p className="muted mb-2.5 text-[0.8125rem]">Everything I have learned about you and your companies, with where it came from. I only use the facts you confirmed. Correct or remove anything that is wrong.</p>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <input className="input min-w-0 flex-1 basis-60" value={newFact} placeholder="Tell me a fact to remember (e.g. IFT's year end is 31 December)" onChange={(e) => setNewFact(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && newFact.trim()) void addFact(); }} />
              <button className="btn btn-ghost" disabled={!newFact.trim()} onClick={() => void addFact()}><Plus size={13} /> Add</button>
            </div>
            {proposed.length > 0 && (
              <>
                <div className="mb-1 flex items-center gap-1.5 text-[0.8125rem]">
                  <strong>New — waiting for your yes</strong>
                  <button className={clsx(mini, "ml-auto")} onClick={() => void decideFacts(proposed.map((k) => k.id), true)}>Remember all {proposed.length}</button>
                </div>
                {proposed.map(factRow)}
              </>
            )}
            <div className="mb-1 mt-3.5 text-[0.8125rem]"><strong>What I know ({confirmed.length})</strong></div>
            {[...new Set(confirmed.map((k) => k.entity || "General"))].sort().map((ent) => (
              <div key={ent}>
                <div className={sideLabel}>{ent}</div>
                {confirmed.filter((k) => (k.entity || "General") === ent).map(factRow)}
              </div>
            ))}
            {!confirmed.length && <p className="muted py-4 text-sm">Nothing confirmed yet. Upload a document or tell me something, and I will propose what is worth keeping.</p>}
          </div>
        )}
      </section>
    </div>
  );
}

/** One assistant answer: the text, where it came from, web sources, facts worth remembering, asset-update chips. */
function Answer({ m, facts, factRow, onRememberAll, onApply }: {
  m: CfoMessage; facts: CfoFact[]; factRow: (k: CfoFact) => ReactNode; onRememberAll: (ids: string[]) => void;
  onApply: (idx: number, a: { name: string; value: number; note?: string }) => void;
}) {
  const ids = m.factIds || [];
  const mine = facts.filter((k) => ids.includes(k.id) && k.status === "proposed");
  const kept = facts.filter((k) => ids.includes(k.id) && k.status === "confirmed").length;
  const cost = m.usage?.costUsd;
  return (
    <div className="max-w-full rounded-xl bg-[var(--panel-2)] px-3 py-2 text-[0.84rem] leading-relaxed ring-1 ring-[var(--line)] sm:max-w-[92%]">
      <CfoMarkdown text={m.content} />
      <div className="mt-1.5 flex flex-wrap items-center gap-x-1 text-[11px] text-[var(--muted)]">
        {fmtTime(m.ts)} · {modelName(m.model)}{m.deep ? " · deep" : ""}{m.fellBack ? " · OpenAI did not answer, Claude did" : ""}
        {!!m.calcs && <> · {m.calcs} figure{m.calcs === 1 ? "" : "s"} calculated by the app</>}
        {!!m.calcFailed && <span className={warn}> · {m.calcFailed} could not be calculated</span>}
        {cost != null && <> · ${cost < 0.01 ? cost.toFixed(4) : cost.toFixed(3)}</>}
        {!!m.unverified?.length && <span className={warn} title="These figures were written by the model itself. They are not in your data and were not calculated by the app, so check them before relying on them."> · not checked: {m.unverified.join(", ")}</span>}
        {!!m.sources?.passages?.length && <> · read from {m.sources.passages.join(", ")}</>}
      </div>
      {m.web?.used && (
        <div className="mt-2 border-t border-[var(--line)] pt-1.5 text-[11.5px] text-[var(--text-2)]">
          <div className="flex items-start gap-1.5">
            <Globe size={12} className="mt-0.5 shrink-0" />
            <span>
              Looked up on the web{m.web.questions?.length ? ":" : ""} {m.web.questions?.map((x) => `"${x}"`).join(" · ")}
              {m.web.pages?.length ? `${m.web.questions?.length ? " · " : " "}read ${m.web.pages.length} page${m.web.pages.length === 1 ? "" : "s"} you linked` : ""}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-1.5 gap-y-1">
            {(m.web.sources || []).map((x) => <a key={x.url} href={x.url} target="_blank" rel="noreferrer noopener" title={x.url} className="rounded-full bg-brand-50 px-2 text-brand-700 hover:underline">{x.host || x.title}</a>)}
          </div>
          {!!m.web.unsourced?.length && <div className={clsx(warn, "mt-1")}>No source was given for: {m.web.unsourced.map((x) => `"${x}"`).join(" · ")}. Treat that part as unconfirmed.</div>}
          {!!m.web.errors?.length && <div className={clsx(warn, "mt-1")}>Part of the lookup failed: {m.web.errors.join(" · ")}</div>}
        </div>
      )}
      {m.web?.on && !m.web.used && !!m.web.errors?.length && <div className={clsx(warn, "mt-1.5 flex items-center gap-1 text-[11px]")}><Globe size={11} /> A web lookup failed: {m.web.errors[0]}</div>}
      {ids.length > 0 && (mine.length ? (
        <div className="mt-2 border-t border-[var(--line)] pt-1.5">
          <div className="mb-1 flex items-center gap-1.5 text-[0.8125rem]">
            <Brain size={13} /> Worth remembering?
            <button className={clsx(mini, "ml-auto")} onClick={() => onRememberAll(mine.map((k) => k.id))}>Remember all {mine.length}</button>
          </div>
          {mine.map(factRow)}
        </div>
      ) : kept ? (
        <div className="mt-1.5 flex items-center gap-1 text-[11px] text-[var(--muted)]"><Check size={11} /> {kept} fact{kept === 1 ? "" : "s"} remembered</div>
      ) : null)}
      {(m.actions || []).map((a, i) => (
        <button
          key={i}
          className={clsx("mr-1.5 mt-2 inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs", m.applied?.[i] ? "cursor-default border-[var(--line-strong)] text-emerald-800" : "border-brand-600 bg-[var(--panel)] text-brand-600 hover:bg-brand-50")}
          disabled={!!m.applied?.[i]}
          onClick={() => onApply(i, a)}
        >
          {m.applied?.[i] ? <><Check size={12} className="shrink-0" /> {m.applied[i]}</> : <><ChevronRight size={12} className="shrink-0" /> Update {a.name} to {eur(a.value)}</>}
        </button>
      ))}
    </div>
  );
}
